<template>
    <div ref="rootRef">
        <slot
            :open-menu="handleOpenMenu"
            :start-value="formattedStart"
            :end-value="formattedEnd"
        >
            <div class="flex gap-4" v-if="!showDefaultInput">
                <input
                    :value="formattedStart"
                    readonly
                    class="block w-full rounded border border-slate-200 bg-white dark:bg-slate-800 py-1.5 px-3 text-gray-900 placeholder:text-slate-500 focus:ring-0 focus:border-slate-800 hover:border-slate-800 text-sm placeholder:text-sm cursor-pointer"
                    placeholder="Start date"
                />
                <input
                    :value="formattedEnd"
                    readonly
                    class="block w-full rounded border border-slate-200 bg-white dark:bg-slate-800 py-1.5 px-3 text-gray-900 placeholder:text-slate-500 focus:ring-0 focus:border-slate-800 hover:border-slate-800 text-sm placeholder:text-sm cursor-pointer"
                    placeholder="End date"
                />
            </div>
        </slot>
        <VueDatePicker
            v-if="isMounted"
            ref="dpRef"
            v-model="internalRange"
            :range="{ partialRange: partialRange }"
            :multi-calendars="multiCalendars"
            :auto-apply="true"
            :config="{ closeOnAutoApply: false }"
            :time-config="{ enableTimePicker: false }"
            :min-date="effectiveMinDate"
            :max-date="effectiveMaxDate"
            :format="formatDateForDisplay"
            :teleport="true"
            :class="
                showDefaultInput
                    ? 'safe-date-range-picker-with-input'
                    : 'safe-date-range-picker-hidden'
            "
            @update:model-value="onRangeChange"
            @range-start="onRangeStart"
            @open="$emit('on-open')"
            @closed="onMenuClosed"
        >
            <template #input-icon v-if="showDefaultInput">
                <slot name="input-icon">
                    <svg
                        class="dp__input_icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <rect
                            x="3"
                            y="4"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                        ></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                </slot>
            </template>
        </VueDatePicker>
    </div>
</template>

<script setup>
import {
    ref,
    computed,
    onMounted,
    onBeforeUnmount,
    watch,
    nextTick,
} from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
    flatpickrFormatToDayjs,
    resolveDateRef,
} from '@/utils/date-format-mapper';
import {
    resolveRangeStartCommit,
    shouldRejectCollapsedRange,
} from '@/utils/date-range-picker-guards';

dayjs.extend(customParseFormat);

const props = defineProps({
    startDate: {
        type: String,
        default: '',
    },
    endDate: {
        type: String,
        default: '',
    },
    config: {
        type: Object,
        default: () => ({}),
    },
    autoRangeDays: {
        type: Number,
        default: null,
    },
    multiCalendars: {
        type: Boolean,
        default: true,
    },
    partialRange: {
        type: Boolean,
        default: false,
    },
    showDefaultInput: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    'update:startDate',
    'update:endDate',
    'range-change',
    'on-open',
    'on-close',
]);

const dpRef = ref(null);
const rootRef = ref(null);
const isMounted = ref(false);

/**
 * Which boundary input ('start' | 'end') opened the picker, or null when the
 * menu was opened without an anchor. While anchored, a single calendar click
 * commits that boundary and keeps the other one fixed (SUPPORT-617) — the
 * behaviour the two-input UI visually promises.
 */
const anchorBoundary = ref(null);

const dayjsFormat = computed(() => {
    return flatpickrFormatToDayjs(props.config?.dateFormat);
});

const resolvedMinDate = computed(() => resolveDateRef(props.config?.minDate));
const resolvedMaxDate = computed(() => resolveDateRef(props.config?.maxDate));

/**
 * When the picker is anchored to a boundary input, dates that would cross the
 * fixed boundary are not valid edits — clicking the End input shouldn't let
 * the user pick a date earlier than Start, and vice versa (SUPPORT-617).
 * Tightening minDate / maxDate while anchored makes the invalid cells visibly
 * disabled instead of silently swapping the pair behind the user's back.
 */
const anchoredMinDate = computed(() => {
    if (props.autoRangeDays !== null && props.autoRangeDays > 0) {
        return null;
    }

    if (anchorBoundary.value !== 'end') {
        return null;
    }

    return parseDate(props.startDate);
});
const anchoredMaxDate = computed(() => {
    if (props.autoRangeDays !== null && props.autoRangeDays > 0) {
        return null;
    }

    if (anchorBoundary.value !== 'start') {
        return null;
    }

    return parseDate(props.endDate);
});

const tighterMin = (a, b) => {
    if (!a) {
        return b;
    }

    if (!b) {
        return a;
    }

    return a.getTime() >= b.getTime() ? a : b;
};
const tighterMax = (a, b) => {
    if (!a) {
        return b;
    }

    if (!b) {
        return a;
    }

    return a.getTime() <= b.getTime() ? a : b;
};

const effectiveMinDate = computed(() =>
    tighterMin(resolvedMinDate.value, anchoredMinDate.value)
);
const effectiveMaxDate = computed(() =>
    tighterMax(resolvedMaxDate.value, anchoredMaxDate.value)
);

/**
 * Parse a date string prop into a Date object.
 */
const parseDate = (val) => {
    if (!val || val === '') {
        return null;
    }

    if (val instanceof Date) {
        return val;
    }

    const parsed = dayjs(val, dayjsFormat.value, true);

    if (parsed.isValid()) {
        return parsed.toDate();
    }

    const fallback = dayjs(val);

    if (fallback.isValid()) {
        return fallback.toDate();
    }

    return null;
};

/**
 * Format a Date object back to the string format consumers expect.
 */
const formatToString = (date) => {
    if (!date) {
        return '';
    }

    return dayjs(date).format(dayjsFormat.value);
};

/**
 * Format function used by VueDatePicker for display.
 */
const formatDateForDisplay = (dates) => {
    if (!dates) {
        return '';
    }

    if (Array.isArray(dates)) {
        const parts = dates.map((d) =>
            d ? dayjs(d).format(dayjsFormat.value) : ''
        );

        return parts.join(' — ');
    }

    return dayjs(dates).format(dayjsFormat.value);
};

/**
 * Build the internal range from start/end props.
 */
const buildRange = () => {
    const start = parseDate(props.startDate);
    const end = parseDate(props.endDate);

    if (start && end) {
        return [start, end];
    }

    if (start && !end) {
        return props.partialRange ? [start] : null;
    }

    return null;
};

const internalRange = ref(buildRange());

const formattedStart = computed(() => {
    if (!internalRange.value || !internalRange.value[0]) {
        return '';
    }

    return formatToString(internalRange.value[0]);
});

const formattedEnd = computed(() => {
    if (!internalRange.value || !internalRange.value[1]) {
        return '';
    }

    return formatToString(internalRange.value[1]);
});

/**
 * Watch props and sync to internal range when they change externally.
 */
watch([() => props.startDate, () => props.endDate], () => {
    const newRange = buildRange();
    const currentStart = formatToString(internalRange.value?.[0]);
    const currentEnd = formatToString(internalRange.value?.[1]);
    const newStart = formatToString(newRange?.[0]);
    const newEnd = formatToString(newRange?.[1]);

    if (currentStart !== newStart || currentEnd !== newEnd) {
        internalRange.value = newRange;
    }
});

/**
 * Handle range selection from the datepicker.
 */
const onRangeChange = (val) => {
    if (!val || !Array.isArray(val) || val.length < 2) {
        emit('update:startDate', '');
        emit('update:endDate', '');
        emit('range-change', null, null);
        return;
    }

    const newStart = val[0];
    const newEnd = val[1];

    // SUPPORT-617: reject the "accidental collapse" pattern. When the user
    // clicks a date outside the existing range, VueDatePicker silently resets
    // and a second click on that same date commits a one-day range, wiping
    // out the boundary the user never intended to touch.
    const prevStart = parseDate(props.startDate);
    const prevEnd = parseDate(props.endDate);

    if (shouldRejectCollapsedRange(prevStart, prevEnd, newStart, newEnd)) {
        internalRange.value = [prevStart, prevEnd];
        return;
    }

    const startStr = formatToString(newStart);
    const endStr = formatToString(newEnd);

    emit('update:startDate', startStr);
    emit('update:endDate', endStr);
    emit('range-change', startStr, endStr);
};

/**
 * Handle range-start event (the first calendar click of a range selection).
 *
 * autoRangeDays takes precedence: when set, every pick auto-fills
 * end date = start + N days regardless of the anchor (the alert form's
 * contract). Otherwise, when the menu is anchored to a boundary input and
 * the opposite boundary exists, the single click commits immediately — the
 * clicked date replaces the anchored boundary, the other stays fixed, and
 * the menu closes. With neither, VueDatePicker's normal two-click range
 * selection proceeds.
 */
const onRangeStart = (startDate) => {
    if (!startDate) {
        return;
    }

    const commit = resolveRangeStartCommit(
        props.autoRangeDays,
        anchorBoundary.value,
        parseDate(props.startDate),
        parseDate(props.endDate),
        new Date(startDate)
    );

    if (!commit) {
        return;
    }

    nextTick(() => {
        internalRange.value = commit.range;
        onRangeChange(commit.range);

        if (commit.anchored) {
            dpRef.value?.closeMenu();
        }
    });
};

/**
 * Open the datepicker menu via the component ref. Accepts an optional
 * boundary ('start' | 'end') to anchor the selection to that input; any
 * other argument (e.g. the MouseEvent from a bare @click="openMenu") leaves
 * the menu unanchored.
 */
const handleOpenMenu = (boundary = null) => {
    if (boundary === 'start' || boundary === 'end') {
        anchorBoundary.value = boundary;
    }

    dpRef.value?.openMenu();
};

/**
 * Delegated click handler on the root wrapper.
 * Opens the datepicker when a readonly input (date field) is clicked, and
 * anchors the selection to that input by position: every consumer renders
 * the Start input first and the End input second.
 */
const onRootClick = (event) => {
    const target = event.target;

    if (target.tagName === 'INPUT' && target.readOnly) {
        const readonlyInputs = [
            ...rootRef.value.querySelectorAll('input[readonly]'),
        ];
        const boundary =
            ['start', 'end'][readonlyInputs.indexOf(target)] ?? null;

        anchorBoundary.value = boundary;
        handleOpenMenu(boundary);
    }
};

/**
 * Reset the anchor when the menu closes so a later programmatic open
 * doesn't inherit a stale boundary.
 */
const onMenuClosed = () => {
    anchorBoundary.value = null;
    emit('on-close');
};

onMounted(() => {
    isMounted.value = true;
    rootRef.value?.addEventListener('click', onRootClick);
});

onBeforeUnmount(() => {
    rootRef.value?.removeEventListener('click', onRootClick);
});
</script>

<style scoped>
.safe-date-range-picker-hidden :deep(.dp__input_wrap) {
    position: absolute;
    width: 0;
    height: 0;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
}

.safe-date-range-picker-with-input :deep(.dp__input) {
    border-color: #e2e8f0;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem 0.375rem 2.5rem;
}

.safe-date-range-picker-with-input :deep(.dp__input:hover),
.safe-date-range-picker-with-input :deep(.dp__input:focus) {
    border-color: #1e293b;
}
</style>
